/*
  Canadian Home and Garden -- product page behaviour.

  One file rather than three inline scripts, because the three components are not independent:
  the fabric a customer picks decides the grade, the grade decides the price, and the fabric's
  yardage decides the lead time. Splitting that across blocks meant either duplicating the
  fabric map three times or depending on block order, and block order is editable in the theme
  editor.

  Each Liquid block emits a <script type="application/json"> config and this boots off whatever
  it finds, so blocks can be reordered, removed, or used on their own.

  THE ONE THING THAT MATTERS: fabric is a line-item PROPERTY, not a variant. Grade is the only
  variant axis, because grade is the only thing that changes the price. Choosing a fabric here
  therefore has to reach into Dawn's variant picker and select the matching grade, or the
  customer sees a Grade 5 price on a Grade 30 fabric.
*/
(function () {
  'use strict';

  var CHAG = (window.CHAG = window.CHAG || {});
  if (CHAG.booted) return;
  CHAG.booted = true;

  // ------------------------------------------------------------------ helpers

  function readConfig(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function plural(n, one, many) {
    return n === 1 ? one : many;
  }

  // The colour a chip shows. Swatch colours, not brand colours: these stand for the fabric
  // families, so a "Red" chip must look like the red fabrics do, not like the logo red.
  var CHIP_HEX = {
    Beige: '#D8C9AE',
    Black: '#2A2A2A',
    Blue: '#4A6E8A',
    Brown: '#7A5B41',
    Green: '#6E8055',
    Grey: '#9AA0A6',
    Purple: '#6E5A78',
    Red: '#9B3B32',
    White: '#F2F0EB',
    Yellow: '#D9B25A',
  };

  // ------------------------------------------------------- add-to-cart gating

  /*
    Two separate things can block an add to cart: no fabric chosen, and a postal code we
    cannot deliver to. Dawn re-enables its own submit button whenever the variant changes,
    so fighting it over the disabled attribute loses. Instead the gate is enforced on submit
    from a capture listener on document, which runs before any listener on the form itself.
  */
  var gates = {};

  CHAG.setGate = function (key, reason, focusEl) {
    if (reason) gates[key] = { reason: reason, focus: focusEl || null };
    else delete gates[key];
  };

  function firstGate() {
    var keys = Object.keys(gates);
    return keys.length ? gates[keys[0]] : null;
  }

  function gateMessageEl(form) {
    var box = form.querySelector('[data-chag-gate-msg]');
    if (box) return box;
    box = el('p', 'chag-dc__msg is-no');
    box.setAttribute('data-chag-gate-msg', '');
    box.setAttribute('role', 'alert');
    var buttons = form.querySelector('.product-form__buttons') || form;
    buttons.parentNode.insertBefore(box, buttons);
    return box;
  }

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      var g = firstGate();
      if (!g) {
        var old = form.querySelector('[data-chag-gate-msg]');
        if (old) old.textContent = '';
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      var box = gateMessageEl(form);
      box.textContent = g.reason;
      if (g.focus) {
        g.focus.scrollIntoView({ block: 'center' });
        if (g.focus.focus) g.focus.focus();
      }
    },
    true
  );

  // ---------------------------------------------------------------- lead time

  /*
    Ported from the live theme's product-template.liquid. The arithmetic is deliberately
    unchanged, because it is what the business has been quoting to customers for years and
    what NLI plans against. Only two things are new: it runs against the fabric the customer
    picked here rather than a fabric-as-variant, and it says WHY the wait is long.

      shop.global_production_wks_stylus   P   weeks Stylus takes to build
      shop.global_delivery_wks_nli        N   weeks NLI takes to deliver
      shop.global_current_prod_wk_stylus  C   where Stylus is in its production cycle
      product.production_week             W   the week of the cycle this frame is built in
      product.stylus_fast_four            F4  frames on the four week express programme
      product.fabric_yard_requirement     YR  yards for one piece; the check uses YR * 2
  */
  function leadTimeFor(cfg, fabric) {
    var P = cfg.productionWeeks,
      N = cfg.nliWeeks,
      C = cfg.currentWeek,
      W = cfg.productionWeek,
      F4 = cfg.fastFour === 1;
    var need = cfg.yardRequirement * 2;

    function inStockWeeks(current) {
      if (F4) return current < 4 ? 4 - current + P + N : 6 - current + W + P + N;
      return W > current ? W - current + P + N : P - current + W + P + N;
    }

    if (!fabric) return { state: 'base', weeks: inStockWeeks(C) };

    if (fabric.y > need) return { state: 'ok', weeks: inStockWeeks(C) };

    if (fabric.o > need) {
      // Stylus works a six week cycle. Waiting for cloth to land moves where in that cycle
      // the frame gets built, so the current week has to be advanced before the same sum.
      var extra = fabric.w;
      var step = (extra % 6) + C;
      var newC = step > 6 ? step - 6 : step;
      var weeks = F4
        ? newC < 4
          ? 4 - newC + extra + P + N
          : 6 - newC + extra + W + P + N
        : W > newC
        ? extra + W - newC + P + N
        : extra + P - newC + W + P + N;
      return { state: 'warn', weeks: weeks, extra: extra, base: inStockWeeks(C) };
    }

    return { state: 'stop', weeks: null, base: inStockWeeks(C) };
  }

  // ------------------------------------------------------------ fabric picker

  function bootFabricPicker() {
    var root = document.querySelector('[data-chag-fabric]');
    var cfg = readConfig('chag-fabric-config');
    if (!root || !cfg || !cfg.map || !cfg.map.fabrics || !cfg.map.fabrics.length) return null;

    var map = cfg.map;
    var base = map.b || '';
    var colours = map.cols || [];
    var need = cfg.yardRequirement * 2;

    // Only offer fabrics whose grade this product actually sells. A Grade 40 fabric on a
    // frame that stops at Grade 30 has no variant to select and no price to show.
    var offered = {};
    (cfg.offeredGrades || []).forEach(function (g) {
      offered[g] = true;
    });
    var fabrics = map.fabrics.filter(function (f) {
      return offered[f.g];
    });
    if (!fabrics.length) return null;

    var state = { main: null, pillow: null };
    var hiddenMain = root.querySelector('[data-chag-prop-main]');
    var hiddenPillow = root.querySelector('[data-chag-prop-pillow]');

    // ---- grade selection in Dawn's variant picker

    function gradeInputs() {
      var name = cfg.gradeOptionName;
      var out = [];
      document.querySelectorAll('variant-selects input[type="radio"]').forEach(function (i) {
        if ((i.dataset.optionName || '') === name) out.push(i);
      });
      document.querySelectorAll('variant-selects select').forEach(function (s) {
        if (s.name === 'options[' + name + ']') out.push(s);
      });
      return out;
    }

    function selectGrade(grade) {
      var want = 'grade ' + grade;
      var hit = false;
      gradeInputs().forEach(function (node) {
        if (node.tagName === 'SELECT') {
          for (var i = 0; i < node.options.length; i++) {
            if (node.options[i].value.trim().toLowerCase() === want) {
              if (node.selectedIndex !== i) {
                node.selectedIndex = i;
                node.dispatchEvent(new Event('change', { bubbles: true }));
              }
              hit = true;
              break;
            }
          }
        } else if (node.value.trim().toLowerCase() === want) {
          if (!node.checked) {
            node.checked = true;
            node.dispatchEvent(new Event('change', { bubbles: true }));
          }
          hit = true;
        }
      });
      return hit;
    }

    /*
      Grade is an internal pricing band, not a customer choice, and leaving it visible
      creates a state the price cannot survive: pick a Grade 15 fabric, then click the
      Grade 30 button, and the line item says one thing while the price says another.
      Hidden from JS rather than from CSS, so a customer without JS still gets Dawn's
      native picker instead of a page with no way to choose.

      It has to be re-hidden after every variant change. Dawn does not update the variant
      picker in place, it REPLACES the whole variant-selects element with markup fetched
      from the server, which threw away the first version of this and put the grade buttons
      back the moment a fabric was picked. The stylesheet rule stops the flash and the
      observer is what actually guarantees it.
    */
    function hideGradeFieldset() {
      gradeInputs().forEach(function (node) {
        var wrap = node.closest('fieldset, .product-form__input');
        if (wrap) wrap.style.display = 'none';
      });
    }

    function keepGradeHidden() {
      hideGradeFieldset();
      var host = document.querySelector('product-info') || document.body;
      host.classList.add('chag-grade-hidden');
      new MutationObserver(function () {
        hideGradeFieldset();
      }).observe(host, { childList: true, subtree: true });
    }

    // ---- the chosen-fabric card, the part that stays next to the price

    var chosen = root.querySelector('[data-chag-chosen]');
    var pillowLabel = root.querySelector('[data-chag-pillow-label]');

    function renderChosen() {
      var f = state.main;
      chosen.innerHTML = '';
      if (!f) {
        chosen.appendChild(el('span', 'chag-fab__empty'));
        var w = el('div');
        w.appendChild(el('p', 'chag-fab__chosen-name', 'No fabric chosen yet'));
        w.appendChild(
          el(
            'p',
            'chag-fab__chosen-meta',
            'Your fabric sets the price, so the total will change once you choose.'
          )
        );
        chosen.appendChild(w);
        return;
      }
      var img = new Image();
      img.src = base + f.i + '?width=260';
      img.alt = f.n + ' fabric';
      img.loading = 'lazy';
      chosen.appendChild(img);

      var body = el('div');
      body.appendChild(el('p', 'chag-fab__chosen-name', f.n));
      body.appendChild(
        el(
          'p',
          'chag-fab__chosen-meta',
          'Grade ' + f.g + '. Grade is the fabric price band. Higher grades cost more and are ' +
            'usually heavier, more textured, or more hard wearing.'
        )
      );
      var sample = el('p', 'chag-fab__sample');
      var a = el('a', null, 'Order a free swatch of ' + f.n);
      a.href = 'https://canadianhomeandgarden.ca/products/' + f.h;
      sample.appendChild(a);
      body.appendChild(sample);
      chosen.appendChild(body);
    }

    function renderPillowLabel() {
      pillowLabel.textContent = state.pillow
        ? 'Accent pillows in ' + state.pillow.n + '.'
        : 'Accent pillows will match your main fabric.';
    }

    // ---- a tile

    function tileFor(f, group, onPick) {
      var usable = f.y > need || f.o > need;
      var label = el('label', 'chag-fab__tile' + (usable ? '' : ' chag-fab__tile--off'));

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = group;
      input.value = f.n;
      if (!usable) input.disabled = true;
      label.appendChild(input);

      var img = new Image();
      img.src = base + f.i + '?width=220';
      img.alt = f.n + ', grade ' + f.g;
      img.loading = 'lazy';
      img.decoding = 'async';
      label.appendChild(img);

      label.appendChild(el('span', 'chag-fab__tile-name', f.n));
      var meta = 'Grade ' + f.g;
      if (!usable) meta = 'Not enough left';
      else if (f.y <= need) meta = 'Grade ' + f.g + ', on order';
      label.appendChild(el('span', 'chag-fab__tile-grade', meta));

      input.addEventListener('change', function () {
        if (input.checked) onPick(f);
      });
      return label;
    }

    // ---- one filterable panel, built once per target and moved into the dialog

    function buildPanel(opts) {
      var wrap = el('div');
      var filters = el('div', 'chag-fab__filters');

      var search = document.createElement('input');
      search.type = 'search';
      search.className = 'chag-fab__search';
      search.placeholder = 'Search by name or pattern';
      search.setAttribute('aria-label', 'Search fabrics by name or pattern');
      filters.appendChild(search);

      var grade = document.createElement('select');
      grade.className = 'chag-fab__grade';
      grade.setAttribute('aria-label', 'Filter fabrics by grade');
      grade.appendChild(new Option('All grades', ''));
      Object.keys(offered)
        .map(Number)
        .sort(function (a, b) {
          return a - b;
        })
        .forEach(function (g) {
          grade.appendChild(new Option('Grade ' + g, String(g)));
        });
      filters.appendChild(grade);

      var chips = el('div', 'chag-fab__colours');
      chips.setAttribute('role', 'group');
      chips.setAttribute('aria-label', 'Filter fabrics by colour');
      var active = {};
      colours.forEach(function (name, ix) {
        if (
          !fabrics.some(function (f) {
            return f.c.indexOf(ix) !== -1;
          })
        ) {
          return;
        }
        var b = el('button', 'chag-fab__chip');
        b.type = 'button';
        b.setAttribute('aria-pressed', 'false');
        var dot = el('i');
        dot.style.background = CHIP_HEX[name] || '#ccc';
        b.appendChild(dot);
        b.appendChild(document.createTextNode(name));
        b.addEventListener('click', function () {
          if (active[ix]) delete active[ix];
          else active[ix] = true;
          b.setAttribute('aria-pressed', active[ix] ? 'true' : 'false');
          render();
        });
        chips.appendChild(b);
      });
      filters.appendChild(chips);
      wrap.appendChild(filters);

      var count = el('p', 'chag-fab__count');
      count.setAttribute('role', 'status');
      wrap.appendChild(count);

      var grid = el('div', 'chag-fab__grid');
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', opts.label);
      wrap.appendChild(grid);

      function render() {
        var q = search.value.trim().toLowerCase();
        var g = grade.value;
        var cols = Object.keys(active).map(Number);
        var list = fabrics.filter(function (f) {
          if (g && String(f.g) !== g) return false;
          if (q && (f.n + ' ' + f.p).toLowerCase().indexOf(q) === -1) return false;
          if (
            cols.length &&
            !cols.some(function (c) {
              return f.c.indexOf(c) !== -1;
            })
          ) {
            return false;
          }
          return true;
        });

        grid.innerHTML = '';
        list.forEach(function (f) {
          grid.appendChild(tileFor(f, opts.group, opts.onPick));
        });
        if (!list.length) {
          grid.appendChild(
            el(
              'p',
              'chag-fab__empty-msg',
              'No fabric matches those filters. Clear one of them to see more.'
            )
          );
        }
        count.textContent =
          list.length + ' ' + plural(list.length, 'fabric', 'fabrics') + ' of ' + fabrics.length;

        // re-check the current choice: the inputs above are new elements
        var cur = opts.current();
        if (cur) {
          grid.querySelectorAll('input').forEach(function (i) {
            if (i.value === cur.n) i.checked = true;
          });
        }
      }

      search.addEventListener('input', render);
      grade.addEventListener('change', render);
      render();
      return wrap;
    }

    // ---- the dialog

    var dialog = root.querySelector('[data-chag-dialog]');
    var dialogBody = root.querySelector('[data-chag-dialog-body]');
    var dialogTitle = root.querySelector('[data-chag-dialog-title]');
    var dialogStatus = root.querySelector('[data-chag-dialog-status]');
    var lastOpener = null;

    var panels = {
      main: {
        title: 'Choose your main fabric',
        node: buildPanel({
          group: 'chag-main-fabric',
          label: 'Main fabric',
          current: function () {
            return state.main;
          },
          onPick: function (f) {
            state.main = f;
            hiddenMain.value = f.n;
            if (!state.pillow) hiddenPillow.value = f.n;
            selectGrade(f.g);
            renderChosen();
            renderPillowLabel();
            dialogStatus.textContent = f.n + ', grade ' + f.g + ' selected.';
            CHAG.setGate('fabric', null);
            if (CHAG.onFabric) CHAG.onFabric(f);
          },
        }),
      },
      pillow: {
        title: 'Choose your accent pillow fabric',
        node: buildPanel({
          group: 'chag-pillow-fabric',
          label: 'Accent pillow fabric',
          current: function () {
            return state.pillow;
          },
          onPick: function (f) {
            state.pillow = f;
            hiddenPillow.value = f.n;
            renderPillowLabel();
            dialogStatus.textContent = f.n + ' selected for the accent pillows.';
          },
        }),
      },
    };

    /*
      showModal puts the dialog in the top layer, which is the whole point: it escapes a
      260px product column and gets the full viewport at every breakpoint. Browsers without
      it fall back to showing the panel inline, which is cramped but still works.
    */
    function openPicker(which, opener) {
      var p = panels[which];
      if (!p) return;
      dialogTitle.textContent = p.title;
      dialogStatus.textContent = '';
      dialogBody.innerHTML = '';
      dialogBody.appendChild(p.node);
      lastOpener = opener || null;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      var firstField = dialogBody.querySelector('.chag-fab__search');
      if (firstField) firstField.focus();
    }

    function closePicker() {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      if (lastOpener && lastOpener.focus) lastOpener.focus();
    }

    root.querySelectorAll('[data-chag-open]').forEach(function (b) {
      b.addEventListener('click', function () {
        openPicker(b.getAttribute('data-chag-open'), b);
      });
    });
    root.querySelector('[data-chag-dialog-close]').addEventListener('click', closePicker);
    root.querySelector('[data-chag-dialog-done]').addEventListener('click', closePicker);
    // clicking the backdrop closes it: the backdrop is the dialog element itself
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) closePicker();
    });

    renderChosen();
    renderPillowLabel();
    keepGradeHidden();
    CHAG.setGate(
      'fabric',
      'Choose your main fabric before adding this to your cart.',
      root.querySelector('[data-chag-open="main"]')
    );

    return { need: need, state: state };
  }


  // ------------------------------------------------------------ lead time UI

  function bootLeadTime() {
    var root = document.querySelector('[data-chag-lead]');
    var cfg = readConfig('chag-lead-config');
    if (!root || !cfg) return;

    var weeksEl = root.querySelector('[data-chag-lead-weeks]');
    var noteEl = root.querySelector('[data-chag-lead-note]');

    function paint(fabric) {
      var r = leadTimeFor(cfg, fabric);
      root.classList.remove('chag-lead--ok', 'chag-lead--warn', 'chag-lead--stop');

      if (r.state === 'base') {
        weeksEl.textContent = 'Get it in ' + r.weeks + ' weeks';
        noteEl.textContent =
          'That is the wait for a fabric we hold in stock. Choose your fabric below and this ' +
          'updates to your exact date range.';
        root.classList.add('chag-lead--ok');
        return;
      }
      if (r.state === 'ok') {
        weeksEl.textContent = 'Get it in ' + r.weeks + ' weeks';
        noteEl.textContent =
          'Your fabric is in stock, so this goes straight into the build queue in Vancouver.';
        root.classList.add('chag-lead--ok');
        return;
      }
      if (r.state === 'warn') {
        weeksEl.textContent = 'Get it in ' + r.weeks + ' weeks';
        /*
          Do not assert that on order means a longer wait. It usually does, but where the
          cloth lands mid cycle it can slot into an earlier build week and come out level or
          even sooner. Saying "this is why the wait is longer" over a smaller number is the
          kind of thing that costs trust on a four month purchase, so the sentence follows
          the arithmetic instead of assuming it.
        */
        if (r.weeks > r.base) {
          noteEl.textContent =
            'This fabric is on order and lands in ' + r.extra + ' ' +
            plural(r.extra, 'week', 'weeks') + ', which is what makes the wait longer. A ' +
            'fabric we already hold would arrive in about ' + r.base + ' weeks.';
          root.classList.add('chag-lead--warn');
        } else {
          noteEl.textContent =
            'This fabric is on order and lands in ' + r.extra + ' ' +
            plural(r.extra, 'week', 'weeks') + '. Your build is scheduled around that, so it ' +
            'still arrives in about ' + r.weeks + ' weeks.';
          root.classList.add('chag-lead--ok');
        }
        return;
      }
      weeksEl.textContent = 'Not available in this fabric';
      noteEl.textContent =
        'There is not enough of this fabric left to cover the frame. Choose another and the ' +
        'wait goes back to about ' + r.base + ' weeks.';
      root.classList.add('chag-lead--stop');
      CHAG.setGate(
        'yardage',
        'That fabric has run short. Please choose another one.',
        document.querySelector('[data-chag-main-grid]')
      );
    }

    CHAG.onFabric = function (f) {
      CHAG.setGate('yardage', null);
      paint(f);
    };
    paint(null);
  }

  // ------------------------------------------------------------- free swatches

  /*
    The limit of 3, ported from the live theme. Its condition is an all free cart holding
    three or more items, so someone who is also buying furniture is not caught by it. Kept
    exactly as it is rather than tightened, because tightening it would silently block a
    customer mid purchase and that is Mark's call to make, not a side effect of a rewrite.

    Read from /cart.js rather than from Liquid alone, because Dawn adds to cart over fetch
    and never reloads the page, so the Liquid count is only ever right on first paint.
  */
  function bootSwatch() {
    var root = document.querySelector('[data-chag-swatch]');
    var cfg = readConfig('chag-swatch-config');
    if (!root || !cfg) return;

    var countEl = root.querySelector('[data-chag-swatch-count]');
    var limit = cfg.limit || 3;

    function relabel(off) {
      document
        .querySelectorAll('form[action*="/cart/add"] button[name="add"]')
        .forEach(function (b) {
          var span = b.querySelector('span:not(.hidden)') || b;
          if (off) {
            if (!b.dataset.chagSwatchLabel) b.dataset.chagSwatchLabel = span.textContent;
            span.textContent = 'Limit of ' + limit + ' reached';
            b.setAttribute('aria-disabled', 'true');
          } else if (b.dataset.chagSwatchLabel) {
            span.textContent = b.dataset.chagSwatchLabel;
            b.setAttribute('aria-disabled', 'false');
            delete b.dataset.chagSwatchLabel;
          }
        });
    }

    function paint(itemCount, totalPrice) {
      var allFree = totalPrice === 0;
      var atLimit = allFree && itemCount >= limit;

      if (!allFree) {
        // there is furniture in the cart, so the free swatch cap does not apply
        countEl.textContent = '';
        CHAG.setGate('swatchlimit', null);
        relabel(false);
        return;
      }

      if (atLimit) {
        countEl.className = 'chag-swatch__count is-limit';
        countEl.textContent =
          'You have ' + itemCount + ' swatches in your cart, which is the limit. Take those out ' +
          'to your room first, and order more once you have seen them.';
        CHAG.setGate(
          'swatchlimit',
          'You already have ' + limit + ' free swatches in your cart.',
          root
        );
        relabel(true);
        return;
      }

      countEl.className = 'chag-swatch__count';
      countEl.textContent = itemCount
        ? itemCount + ' of ' + limit + ' free swatches chosen.'
        : 'Order up to ' + limit + ' free swatches.';
      CHAG.setGate('swatchlimit', null);
      relabel(false);
    }

    function refresh() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (c) {
          paint(c.item_count, c.total_price);
        })
        .catch(function () {});
    }

    paint(cfg.itemCount, cfg.totalPrice);

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }
    // a backstop for the same reason the gate uses a capture listener: Dawn's events are not
    // a contract, and being wrong here means offering unlimited free post
    document.addEventListener(
      'submit',
      function (e) {
        var f = e.target;
        if (f && f.action && f.action.indexOf('/cart/add') !== -1) setTimeout(refresh, 1500);
      },
      false
    );
  }

  // -------------------------------------------------------- delivery check UI

  /*
    Runs BEFORE the customer configures, not at the cart. Spending twenty minutes choosing
    fabric and then being told we do not deliver to your town is the worst version of this
    page. The cart level check stays as the backstop.
  */
  function bootDeliveryCheck() {
    var root = document.querySelector('[data-chag-delivery-check]');
    var cfg = readConfig('chag-delivery-config');
    if (!root || !cfg) return;

    var blocked = (cfg.noship || '').toUpperCase().split('|').filter(Boolean);
    var isSwatch = !!cfg.isSwatch;
    var phone = cfg.phone || '';
    var input = root.querySelector('[data-chag-postal]');
    var msg = root.querySelector('[data-chag-dc-msg]');
    var btn = root.querySelector('[data-chag-check]');
    var KEY = 'chag_postal';

    function fsa(v) {
      return (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
    }

    function relabelButtons(off) {
      document
        .querySelectorAll('form[action*="/cart/add"] button[name="add"]')
        .forEach(function (b) {
          var span = b.querySelector('span:not(.hidden)') || b;
          if (off) {
            if (!b.dataset.chagLabel) b.dataset.chagLabel = span.textContent;
            span.textContent = 'Not available in your area';
            b.setAttribute('aria-disabled', 'true');
          } else if (b.dataset.chagLabel) {
            span.textContent = b.dataset.chagLabel;
            b.setAttribute('aria-disabled', 'false');
          }
        });
    }

    function check(v, quiet) {
      var code = fsa(v);
      if (code.length < 3) {
        if (!quiet) {
          msg.className = 'chag-dc__msg';
          msg.textContent = 'Please enter your postal code.';
        }
        return;
      }
      try {
        localStorage.setItem(KEY, code);
      } catch (e) {}

      if (blocked.indexOf(code) !== -1) {
        msg.className = 'chag-dc__msg is-no';
        msg.innerHTML = isSwatch
          ? 'We are not able to deliver furniture to ' + code + ', so there is no point ' +
            'sending you samples. Our delivery partner does not service this area. Please ' +
            'call <a href="tel:' + phone.replace(/[^0-9+]/g, '') + '">' + phone + '</a> if ' +
            'you think this is a mistake.'
          : 'We are not able to deliver to ' + code + '. Our delivery partner does not ' +
            'service this area, so we cannot complete this order. Please call ' +
            '<a href="tel:' + phone.replace(/[^0-9+]/g, '') + '">' + phone + '</a> if you ' +
            'think this is a mistake.';
        relabelButtons(true);
        CHAG.setGate('delivery', 'We are not able to deliver to ' + code + '.', input);
      } else {
        msg.className = 'chag-dc__msg is-ok';
        msg.textContent = isSwatch
          ? 'Good news, we deliver furniture to ' + code + ', so your free samples are worth ' +
            'ordering.'
          : 'Good news, we deliver to ' + code + '. White glove delivery is included.';
        relabelButtons(false);
        CHAG.setGate('delivery', null);
      }
    }

    btn.addEventListener('click', function () {
      check(input.value);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        check(input.value);
      }
    });

    try {
      var saved = localStorage.getItem(KEY);
      if (saved) {
        input.value = saved;
        check(saved, true);
      }
    } catch (e) {}
  }

  // ---------------------------------------------------------------------- go

  function boot() {
    bootLeadTime();
    bootFabricPicker();
    bootDeliveryCheck();
    bootSwatch();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
